import { useMemo } from 'react';

import { Columns } from '@/components/table/types';
import { useSmartColumnPositioning } from '@/hooks/useSmartColumnPositioning';
import {
  getColumns,
  getSortableColumnsConfig,
} from '@/sections/asset/config/columnDefinitions';
import { reconcileAssetColumns } from '@/sections/asset/config/columns';
import { Asset } from '@/types';

/**
 * Return type for useAssetColumns hook
 */
export interface UseAssetColumnsReturn {
  /** Rendered table columns with current configuration */
  columns: Columns<Asset>;
  /** Currently active column IDs in display order */
  activeColumns: string[] | undefined;
  /** Function to update active columns with user ordering */
  setActiveColumns: (newColumns: string[]) => void;
  /** Placeholder text showing column count (e.g., "Columns (8/12)") */
  placeholder: string;
  /** Column configuration for sortable column component */
  sortableColumnsConfig: ReturnType<typeof getSortableColumnsConfig>;
}

/**
 * Custom hook for managing asset table columns with smart positioning.
 *
 * Uses the shared useSmartColumnPositioning hook to ensure status column
 * returns to first position and date columns to last position when toggled on,
 * while preserving user drag-to-reorder preferences.
 *
 * Handles:
 * - Column definitions and default configuration
 * - Smart positioning: status first, origin 3rd-to-last, tags before dates, dates last, others after identifier
 * - Active vs available column state
 * - Column count placeholder generation
 * - User column ordering preferences
 *
 * Positioning Rules:
 * - Status column → first position when toggled on
 * - Origin column → 3rd-to-last position (before tags and dates) when toggled on
 * - Tags column → before date columns when toggled on
 * - Date columns (created, visited) → last positions when toggled on
 * - Other columns → after identifier when toggled on
 * - User drag-to-reorder preferences are preserved
 *
 * @returns Column configuration state and controls
 *
 * @example
 * ```tsx
 * const { columns, activeColumns, setActiveColumns, placeholder, sortableColumnsConfig } =
 *   useAssetColumns();
 *
 * <Table
 *   columns={columns}
 *   sortableColumns={{
 *     activeColumns,
 *     setActiveColumns,
 *     defaultConfig: sortableColumnsConfig,
 *     placeholder,
 *   }}
 * />
 * ```
 */
export function useAssetColumns(): UseAssetColumnsReturn {
  // Initialize default column definitions
  const defaultColumns: Columns<Asset> = useMemo(
    () => getColumns() as Columns<Asset>,
    []
  );

  // Generate sortable columns configuration
  const sortableColumnsConfig = useMemo(
    () => getSortableColumnsConfig({ defaultColumns }),
    [defaultColumns]
  );

  // Initialize smart column positioning with positioning rules.
  // Note: we intentionally do NOT consume the `columns` it returns — that array
  // is built from the user's raw stored ids. We render `reconciledColumns`
  // (derived below) instead so the immutable lead block always appears.
  const { activeColumns, setActiveColumns } = useSmartColumnPositioning({
    // v7 removes Application from the default column set for all users. This is a
    // one-time reset of saved column prefs so everyone (including users with
    // stored prefs) picks up the new default set; Application stays selectable in
    // the column picker. The immutable lead block
    // (status/severity/riskScore/identifier) is unchanged from v6.
    key: 'assets.v7',
    defaultColumns,
    defaultConfig: sortableColumnsConfig,
    positioningRules: {
      // Immutable leading block — kept first, in this order, on every reorder.
      firstColumns: ['status', 'severity', 'riskScore', 'identifier'],
      beforeLastColumns: ['origins'], // Origin (before dates) when toggled on
      lastColumns: ['created', 'visited'], // Date columns → last when toggled on
      afterColumn: {
        // Non-special columns insert after identifier
        ids: ['asname', 'asnumber', 'country', 'ipVersion', 'attackSurface'],
        after: 'identifier', // Insert after identifier column
      },
    },
  });

  // Read-time reconciliation: a safety net that injects the immutable leading
  // block for any stored list that predates a later immutable addition. The
  // primary migration is the v6 key bump above (which resets everyone's prefs);
  // this stays as a pure-derivation backstop so a future stored v6 list missing
  // a newly-added immutable column still renders it. See reconcileAssetColumns.
  const reconciledActiveColumns = useMemo(
    () => reconcileAssetColumns(activeColumns, sortableColumnsConfig),
    [activeColumns, sortableColumnsConfig]
  );

  // Render the table from the reconciled list, not the raw stored ids. The
  // `columns` returned by useSmartColumnPositioning is built from the user's raw
  // persisted ids, so a stored list missing an immutable column would otherwise
  // see it marked active in the picker but never rendered in the table. Mapping
  // ids → defs mirrors useSortableColumn's own mapping; the asset config has
  // fixed:[] and no fixedLast, so a direct map is equivalent and correct.
  const reconciledColumns = useMemo(
    () =>
      reconciledActiveColumns
        .map(id => defaultColumns.find(c => c.id === id))
        .filter((c): c is Columns<Asset>[number] => c !== undefined),
    [reconciledActiveColumns, defaultColumns]
  );

  // Generate enhanced placeholder showing active/total column counts
  const placeholder = useMemo(() => {
    const total =
      sortableColumnsConfig.active.length +
      sortableColumnsConfig.available.length;
    const active = reconciledActiveColumns.length;
    return `Columns (${active}/${total})`;
  }, [
    reconciledActiveColumns.length,
    sortableColumnsConfig.active.length,
    sortableColumnsConfig.available.length,
  ]);

  return {
    // Both the rendered columns and the picker's current selection are derived
    // from the reconciled (immutable-lead-injected) list, so legacy users see
    // severity/riskScore in the table without manually toggling anything.
    columns: reconciledColumns,
    activeColumns: reconciledActiveColumns,
    // Writes still go through useSmartColumnPositioning unchanged.
    setActiveColumns,
    placeholder,
    sortableColumnsConfig,
  };
}
